import { authService } from '../services';

/**
 * Role-based permissions utility
 * Use this to check if user has access to specific features
 */

const normalizeRole = (role) => {
  if (!role) {
    return null;
  }

  const sanitized = role.trim().toLowerCase().replace(/\s+/g, '_');
  const aliasMap = {
    super: 'super_distributor',
    superdistributor: 'super_distributor',
    'super-distributor': 'super_distributor',
    master: 'master_distributor',
    masterdistributor: 'master_distributor',
    'master-distributor': 'master_distributor'
  };

  return aliasMap[sanitized] || sanitized;
};

// Define role hierarchy (higher number = more permissions)
export const ROLE_HIERARCHY = {
  admin: 100,
  super_distributor: 80,
  super: 80,
  master_distributor: 60,
  master: 60,
  distributor: 40,
  retailer: 20,
  api_partner: 20,
  salesforce: 10,
  employee: 10,
  support: 10
};

/**
 * Check if user has a specific role
 * @param {string} requiredRole - The role to check
 * @returns {boolean}
 */
export const hasRole = (requiredRole) => {
  const userRole = normalizeRole(authService.getUserRole());
  const targetRole = normalizeRole(requiredRole);

  if (!userRole || !targetRole) {
    return false;
  }

  return userRole === targetRole;
};

/**
 * Check if user has any of the specified roles
 * @param {Array<string>} roles - Array of roles to check
 * @returns {boolean}
 */
export const hasAnyRole = (roles) => {
  const userRole = normalizeRole(authService.getUserRole());
  if (!userRole) {
    return false;
  }

  return roles.map(normalizeRole).includes(userRole);
};

/**
 * Check if user is admin
 * @returns {boolean}
 */
export const isAdmin = () => {
  return hasRole('admin');
};

/**
 * Check if user has permission level equal or higher than required
 * @param {string} requiredRole - The minimum role required
 * @returns {boolean}
 */
export const hasMinimumRole = (requiredRole) => {
  const userRole = normalizeRole(authService.getUserRole());
  const targetRole = normalizeRole(requiredRole);

  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[targetRole] || 0;
  return userLevel >= requiredLevel;
};

/**
 * Get user's current role
 * @returns {string|null}
 */
export const getCurrentRole = () => {
  return normalizeRole(authService.getUserRole());
};

/**
 * Check if feature is accessible to current user
 * @param {string} feature - Feature name
 * @returns {boolean}
 */
export const canAccessFeature = (feature) => {
  const userRole = normalizeRole(authService.getUserRole());
  
  // Define feature permissions
  const featurePermissions = {
    'user-management': ['admin'],
    'reports': ['admin', 'super_distributor', 'master_distributor'],
    'transactions': ['admin', 'super_distributor', 'master_distributor', 'distributor', 'retailer'],
    'wallet': ['admin', 'super_distributor', 'master_distributor', 'distributor', 'retailer'],
    'settings': ['admin'],
    'support': ['admin', 'super_distributor', 'support'],
    // Add more features as needed
  };

  const allowedRoles = featurePermissions[feature];
  if (!allowedRoles) return true; // If feature not defined, allow by default
  
  const allowedCanon = allowedRoles ? allowedRoles.map(normalizeRole) : [];
  return allowedCanon.includes(userRole);
};

/**
 * Get display name for role
 * @param {string} role - Role key
 * @returns {string}
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    admin: 'Admin',
    super: 'Super Distributor',
    super_distributor: 'Super Distributor',
    master: 'Master Distributor',
    master_distributor: 'Master Distributor',
    distributor: 'Distributor',
    retailer: 'Retailer',
    api_partner: 'API Partner',
    salesforce: 'Sales Force',
    employee: 'Employee',
    support: 'Support'
  };
  
  return roleNames[role] || role;
};

/**
 * Check if user can perform action on target user
 * @param {string} targetUserRole - Role of the target user
 * @returns {boolean}
 */
export const canManageUser = (targetUserRole) => {
  const currentRole = normalizeRole(authService.getUserRole());
  const targetRole = normalizeRole(targetUserRole);
  const currentLevel = ROLE_HIERARCHY[currentRole] || 0;
  const targetLevel = ROLE_HIERARCHY[targetRole] || 0;
  
  // Can only manage users with lower role level
  return currentLevel > targetLevel;
};

export { normalizeRole };

